import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useNavigate } from 'react-router-dom';
import { AppState } from '../../../../../store';
import { signSetAction } from '../../../../../store/signup/actions';
import { useTranslation } from 'react-i18next';
import {
    Country,
    defaultOrganizationSuggestion,
    LocationSelected,
} from '../../../../../store/signup/types';
import { PATH } from '../../../../../routes';
import { zipRegExp } from '../../../utils/validationRegExp';
import { phoneValidation } from '@givelify/givelify-ui';
import {
    GivelifyButton,
    GivelifyForm,
    GivelifyFormTextField,
} from '@givelify/ui';
import * as Yup from 'yup';
import { CountrySelectorWrapper, FormContent, FormWrapper } from './style';
import { useFormContext } from 'react-hook-form';
import { pushEvent } from 'utils/clevertapUtils';
import {
    CLICK_ADDRESS_INPUT,
    CLICK_MANUAL_ENTRY_CITY_INPUT,
    CLICK_MANUAL_ENTRY_NEXT_BTN,
    CLICK_MANUAL_ENTRY_PHONE_INPUT,
    CLICK_MANUAL_ENTRY_ZIP_INPUT,
    VIEW_ORG_MANUAL_ENTRY_PAGE,
} from 'utils/clevertapEvents';
import { useInvokeApi } from 'pages/signup/utils/useInvokeApi';
import AlreadyRegistered from 'pages/signup/components/SuggestionsList/AlreadyRegistered';
import { FormProps, GivingPartnerResponse } from './types';
import { Features } from 'store/featureFlags/types';
import { LocationAutoComplete } from 'pages/signup/components/LocationAutocomplete/LocationAutocomplete';
import {
    SEARCH_EXPERIMENT,
    SEARCH_UI_EXPERIMENT,
} from 'utils/clevertapVariantFlags';

const getValidationSchema = (locationErrorMessage: string) =>
    Yup.object<FormProps>({
        streetAddress: Yup.string().required(''),
        country: Yup.mixed<Country>().required().oneOf(['us', 'bs']),
        location: Yup.mixed<LocationSelected>().test(
            'location',
            locationErrorMessage,
            (value: LocationSelected) => !!value.city && !!value.state,
        ),
        zip: Yup.string()
            .notRequired()
            .when('country', (country: Country, schema: Yup.StringSchema) =>
                country === 'us'
                    ? schema.test(
                          'zip',
                          '',
                          (value?: string) => !!value && zipRegExp.test(value),
                      )
                    : schema,
            ),
        phone: Yup.string()
            .required('')
            .test('phone', '', (value: string) => phoneValidation(value)),
    });

const OrganizationManualEntryFormV2: React.FC = () => {
    const dispatch = useAppDispatch();
    const push = useNavigate();
    const {
        defaultValueLoc,
        defaultStreetAddress,
        defaultCountry,
        defaultPhone,
        v2SearchAPIEnabled,
    } = useSelector((state: AppState) => ({
        defaultValueLoc: state.Signup.organizationLocation,
        defaultStreetAddress: state.Signup.organizationAddress,
        defaultCountry: state.Signup.organizationCountry,
        defaultPhone: state.Signup.organizationPhoneNumber,
        v2SearchAPIEnabled:
            state.FeatureFlagging.features[Features.SEARCH_API_V2],
    }));
    const [response, makeRequest] = useInvokeApi<GivingPartnerResponse>();
    const [showAlreadyRegistered, setShowAlreadyRegistered] = useState(false);
    const [formData, setFormData] = useState<FormProps | null>(null);

    const { t } = useTranslation();
    const { locationErrorMessage } = useMemo(
        () => ({
            locationErrorMessage: t(
                'signup.organizationSearch.locationErrorMessage',
            ),
        }),
        [t],
    );

    useEffect(() => {
        pushEvent(VIEW_ORG_MANUAL_ENTRY_PAGE, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: 'on',
        });
    }, [v2SearchAPIEnabled]);

    const onSubmit = useCallback(
        (values: FormProps) => {
            pushEvent(CLICK_MANUAL_ENTRY_NEXT_BTN, {
                [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
                [SEARCH_UI_EXPERIMENT]: 'on',
            });
            setFormData(values);
            makeRequest(
                'GET',
                `/signup/search?phone=${encodeURIComponent(values.phone)}`,
            );
        },
        [makeRequest, v2SearchAPIEnabled],
    );

    useEffect(() => {
        if (response.type !== 'REQUEST_SUCCESS' || !formData) return;

        const match = response.data.data[0];
        if (match) {
            dispatch(
                signSetAction({
                    selectedOrganization: {
                        source: {
                            ...defaultOrganizationSuggestion,
                            address: `${match.address} ${match.address2}`,
                            bgaLink: match.ogaUrl,
                            city: match.city,
                            doneeId: match.givingPartnerId.toString(),
                            doneeType: match.givingPartnerType,
                            name: match.givingPartnerName,
                            official: match.faithLeaderName || '',
                            phone: match.phone,
                            pastorAvatar: match.faithLeaderAvatar
                                ? match.faithLeaderAvatar
                                : '',
                            logo: match.photo ? match.photo : '',
                            country: match.country,
                        },
                    },
                }),
            );

            setShowAlreadyRegistered(true);
        } else {
            const { country, location, phone, streetAddress, zip } = formData;
            dispatch(
                signSetAction({
                    organizationAddress: streetAddress,
                    organizationLocation: {
                        ...location,
                        zip: zip || location.zip,
                    },
                    organizationCountry: country,
                    organizationPhoneNumber: phone,
                    organizationZip: zip || location.zip,
                    organizationCity: location.city,
                    organizationState: location.state,
                }),
            );
            push(PATH.SIGNUP.STEPS.TWO);
        }
        // eslint-disable-next-line
    }, [response, dispatch, push]);

    const formSchema = useMemo(
        () => getValidationSchema(locationErrorMessage),
        [locationErrorMessage],
    );

    if (showAlreadyRegistered) {
        return (
            <AlreadyRegistered
                onBackClick={() => {
                    setShowAlreadyRegistered(false);
                }}
            />
        );
    }

    return (
        <FormWrapper container>
            <GivelifyForm<FormProps>
                schema={formSchema}
                reValidateMode="onChange"
                onSubmit={onSubmit}
                defaultValues={{
                    streetAddress: formData?.streetAddress
                        ? formData?.streetAddress
                        : defaultStreetAddress,
                    country: formData?.country
                        ? formData?.country
                        : defaultCountry,
                    location: formData?.location
                        ? formData?.location
                        : defaultValueLoc,
                    zip: formData?.zip ? formData?.zip : defaultValueLoc.zip,
                    phone: formData?.phone ? formData?.phone : defaultPhone,
                }}
                mode="onBlur"
            >
                <View />
            </GivelifyForm>
        </FormWrapper>
    );
};

const View = () => {
    const { t } = useTranslation();
    const copy = useMemo(
        () => ({
            streetAddress: t('signup.manualEntry.streetAddress'),
            zip: t('signup.manualEntry.zip'),
            phone: t('signup.manualEntry.phone'),
            next: t('signup.manualEntry.next'),
        }),
        [t],
    );
    const v2SearchAPIEnabled = useSelector(
        (state: AppState) =>
            state.FeatureFlagging.features[Features.SEARCH_API_V2],
    );

    const {
        watch,
        setValue,
        register,
        formState: { isValid },
    } = useFormContext<FormProps>();

    register('country');

    const country = watch('country');
    const setCountry = (value: Country) => {
        setValue('country', value);
    };

    return (
        <>
            <FormContent className="fs-unmask" container>
                <GivelifyFormTextField<FormProps>
                    placeholder={copy.streetAddress}
                    label={copy.streetAddress}
                    fullWidth
                    size="large"
                    name="streetAddress"
                    maxLength={255}
                    onClick={() =>
                        pushEvent(CLICK_ADDRESS_INPUT, {
                            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled
                                ? 'on'
                                : 'off',
                            [SEARCH_UI_EXPERIMENT]: 'on',
                        })
                    }
                />
                <CountrySelectorWrapper showZip={country === 'us'}>
                    <LocationAutoComplete
                        name="location"
                        onClick={() =>
                            pushEvent(CLICK_MANUAL_ENTRY_CITY_INPUT, {
                                [SEARCH_EXPERIMENT]: v2SearchAPIEnabled
                                    ? 'on'
                                    : 'off',
                                [SEARCH_UI_EXPERIMENT]: 'on',
                            })
                        }
                        country={country}
                        setCountry={setCountry}
                        style={{
                            gridArea: 'city',
                        }}
                    />

                    {country === 'us' && (
                        <GivelifyFormTextField<FormProps>
                            fullWidth
                            label={copy.zip}
                            placeholder={copy.zip}
                            size="large"
                            style={{
                                gridArea: 'zip',
                            }}
                            name="zip"
                            maxLength={5}
                            onClick={() =>
                                pushEvent(CLICK_MANUAL_ENTRY_ZIP_INPUT, {
                                    [SEARCH_EXPERIMENT]: v2SearchAPIEnabled
                                        ? 'on'
                                        : 'off',
                                    [SEARCH_UI_EXPERIMENT]: 'on',
                                })
                            }
                        />
                    )}
                </CountrySelectorWrapper>

                <GivelifyFormTextField<FormProps>
                    fullWidth
                    label={copy.phone}
                    placeholder={copy.phone}
                    size="large"
                    name="phone"
                    maxLength={14}
                    maskOptions={{ mask: '(000) 000-0000' }}
                    onClick={() =>
                        pushEvent(CLICK_MANUAL_ENTRY_PHONE_INPUT, {
                            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled
                                ? 'on'
                                : 'off',
                            [SEARCH_UI_EXPERIMENT]: 'on',
                        })
                    }
                />
            </FormContent>
            <GivelifyButton
                className="fs-unmask"
                text={copy.next}
                variant="primary"
                size="large"
                type="submit"
                disabled={!isValid}
            />
        </>
    );
};

export default React.memo(OrganizationManualEntryFormV2);
